import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { dealInputSchema, type DealInput, calculateDealMetrics } from '@/lib/schema'
import { formatCurrency, formatPercent } from '@/lib/formulas'
import { useState, useCallback } from 'react'
import { ChevronDown, ChevronUp, Search, Loader2 } from 'lucide-react'
import { clsx } from 'clsx'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import { trpcClient } from '@/lib/trpc-client'

interface DealFormProps {
  initialData?: Partial<DealInput>
  onSubmit: (data: DealInput) => Promise<void>
  isSubmitting?: boolean
  submitLabel?: string
}

const defaultValues: DealInput = {
  address: '',
  city: '',
  state: '',
  zip: '',
  propertyType: 'single-family',
  units: 1,
  purchasePrice: 0,
  downPaymentPercent: 20,
  closingCosts: 0,
  rehabCosts: 0,
  interestRate: 7.5,
  loanTermYears: 30,
  monthlyRent: 0,
  otherMonthlyIncome: 0,
  vacancyRate: 5,
  propertyTax: 0,
  insurance: 0,
  hoa: 0,
  maintenance: 0,
  propertyManagement: 0,
  utilities: 0,
  otherExpenses: 0,
  name: '',
  notes: '',
}

interface FormSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function FormSection({ title, children, defaultOpen = true }: FormSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-slate-50"
      >
        <h3 className="font-medium text-slate-900">{title}</h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-400" />
        )}
      </button>
      {isOpen && <div className="border-t border-slate-100 px-5 py-4">{children}</div>}
    </div>
  )
}

interface InputFieldProps {
  label: string
  name: keyof DealInput
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any
  type?: string
  prefix?: string
  suffix?: string
  step?: string
  className?: string
}

function InputField({
  label,
  name,
  register,
  errors,
  type = 'text',
  prefix,
  suffix,
  step,
  className,
}: InputFieldProps) {
  const error = errors[name]

  return (
    <div className={className}>
      <label htmlFor={name} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="relative mt-1">
        {prefix && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
            {prefix}
          </span>
        )}
        <input
          id={name}
          type={type}
          step={step}
          {...register(name, { valueAsNumber: type === 'number' })}
          className={clsx(
            'block w-full rounded-lg border border-slate-300 py-2 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
            prefix ? 'pl-7' : 'pl-3',
            suffix ? 'pr-12' : 'pr-3',
            error && 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500">
            {suffix}
          </span>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-danger-600">{error.message as string}</p>}
    </div>
  )
}

export function DealForm({ initialData, onSubmit, isSubmitting, submitLabel = 'Analyze Deal' }: DealFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(dealInputSchema),
    defaultValues: { ...defaultValues, ...initialData } as DealInput,
  })

  const [isLookingUp, setIsLookingUp] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  // Watch values for live preview
  const watchedValues = watch()
  const hasEnoughData =
    watchedValues.purchasePrice > 0 && watchedValues.monthlyRent > 0

  const metrics = hasEnoughData ? calculateDealMetrics(watchedValues) : null

  // Handle address autocomplete selection
  const handleAddressSelect = useCallback(
    (components: { address: string; city: string; state: string; zip: string }) => {
      setValue('city', components.city)
      setValue('state', components.state)
      setValue('zip', components.zip)
    },
    [setValue]
  )

  // Lookup property data from RapidAPI
  const handlePropertyLookup = async () => {
    const address = watchedValues.address
    const city = watchedValues.city
    const state = watchedValues.state

    if (!address || !city || !state) {
      setLookupError('Please enter address, city, and state first')
      return
    }

    setIsLookingUp(true)
    setLookupError(null)

    try {
      const data = await trpcClient.property.lookup.query({ address, city, state })

      if (data.price) setValue('purchasePrice', data.price)
      if (data.bedrooms) setValue('units', Math.max(1, Math.ceil(data.bedrooms / 2)))
      if (data.yearBuilt) setValue('yearBuilt', data.yearBuilt)
      if (data.sqft) setValue('sqft', data.sqft)
      if (data.propertyType) {
        const typeMap: Record<string, DealInput['propertyType']> = {
          'single_family': 'single-family',
          'single family': 'single-family',
          'duplex': 'duplex',
          'triplex': 'triplex',
          'fourplex': 'fourplex',
          'quadplex': 'fourplex',
        }
        const mappedType = typeMap[data.propertyType.toLowerCase()]
        if (mappedType) setValue('propertyType', mappedType)
      }
      // Estimate monthly property tax from annual
      if (data.propertyTax) setValue('propertyTax', Math.round(data.propertyTax / 12))
      // Estimate rent if available
      if (data.rentEstimate) setValue('monthlyRent', data.rentEstimate)
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Failed to lookup property')
    } finally {
      setIsLookingUp(false)
    }
  }

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data as DealInput))} className="space-y-6">
      {/* Live Preview */}
      {metrics && (
        <div className="rounded-xl border border-primary-200 bg-primary-50 p-5">
          <h3 className="font-medium text-primary-900">Live Preview</h3>
          <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-xs text-primary-600">Cap Rate</p>
              <p className="font-mono text-lg font-semibold text-primary-900">
                {formatPercent(metrics.capRate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-600">Cash-on-Cash</p>
              <p className="font-mono text-lg font-semibold text-primary-900">
                {formatPercent(metrics.cashOnCash)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-600">Monthly Cash Flow</p>
              <p className={clsx(
                'font-mono text-lg font-semibold',
                metrics.monthlyCashFlow >= 0 ? 'text-success-600' : 'text-danger-600'
              )}>
                {formatCurrency(metrics.monthlyCashFlow)}
              </p>
            </div>
            <div>
              <p className="text-xs text-primary-600">DSCR</p>
              <p className="font-mono text-lg font-semibold text-primary-900">
                {metrics.dscr.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Property Info */}
      <FormSection title="Property Information">
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Deal Name (optional)"
            name="name"
            register={register}
            errors={errors}
            className="md:col-span-2"
          />

          {/* Address with Autocomplete */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-slate-700">
              Street Address
            </label>
            <div className="mt-1 flex gap-2">
              <div className="flex-1">
                <AddressAutocomplete
                  value={watchedValues.address || ''}
                  onChange={(value) => setValue('address', value)}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing an address..."
                  error={!!errors.address}
                />
              </div>
              <button
                type="button"
                onClick={handlePropertyLookup}
                disabled={isLookingUp}
                className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title="Lookup property details"
              >
                {isLookingUp ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Lookup
              </button>
            </div>
            {errors.address && (
              <p className="mt-1 text-sm text-danger-600">{errors.address.message as string}</p>
            )}
            {lookupError && (
              <p className="mt-1 text-sm text-danger-600">{lookupError}</p>
            )}
          </div>

          <InputField label="City" name="city" register={register} errors={errors} />
          <div className="grid grid-cols-2 gap-4">
            <InputField label="State" name="state" register={register} errors={errors} />
            <InputField label="ZIP" name="zip" register={register} errors={errors} />
          </div>
          <div>
            <label htmlFor="propertyType" className="block text-sm font-medium text-slate-700">
              Property Type
            </label>
            <select
              id="propertyType"
              {...register('propertyType')}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="single-family">Single Family</option>
              <option value="duplex">Duplex</option>
              <option value="triplex">Triplex</option>
              <option value="fourplex">Fourplex</option>
            </select>
          </div>
          <InputField
            label="Units"
            name="units"
            type="number"
            register={register}
            errors={errors}
            step="1"
          />
        </div>
      </FormSection>

      {/* Purchase Details */}
      <FormSection title="Purchase Details">
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Purchase Price"
            name="purchasePrice"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Down Payment"
            name="downPaymentPercent"
            type="number"
            suffix="%"
            step="0.1"
            register={register}
            errors={errors}
          />
          <InputField
            label="Closing Costs"
            name="closingCosts"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Rehab Costs"
            name="rehabCosts"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
        </div>
      </FormSection>

      {/* Loan Terms */}
      <FormSection title="Loan Terms">
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Interest Rate"
            name="interestRate"
            type="number"
            suffix="%"
            step="0.125"
            register={register}
            errors={errors}
          />
          <InputField
            label="Loan Term"
            name="loanTermYears"
            type="number"
            suffix="years"
            register={register}
            errors={errors}
          />
        </div>
      </FormSection>

      {/* Income */}
      <FormSection title="Income">
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Monthly Rent"
            name="monthlyRent"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Other Monthly Income"
            name="otherMonthlyIncome"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Vacancy Rate"
            name="vacancyRate"
            type="number"
            suffix="%"
            step="0.5"
            register={register}
            errors={errors}
          />
        </div>
      </FormSection>

      {/* Expenses */}
      <FormSection title="Monthly Expenses" defaultOpen={false}>
        <div className="grid gap-4 md:grid-cols-2">
          <InputField
            label="Property Tax (monthly)"
            name="propertyTax"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Insurance"
            name="insurance"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="HOA"
            name="hoa"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Maintenance"
            name="maintenance"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Property Management"
            name="propertyManagement"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Utilities"
            name="utilities"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
          <InputField
            label="Other Expenses"
            name="otherExpenses"
            type="number"
            prefix="$"
            register={register}
            errors={errors}
          />
        </div>
      </FormSection>

      {/* Notes */}
      <FormSection title="Notes" defaultOpen={false}>
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
            Notes
          </label>
          <textarea
            id="notes"
            rows={4}
            {...register('notes')}
            className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="Add any notes about this deal..."
          />
        </div>
      </FormSection>

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-lg bg-primary-600 px-6 py-2.5 font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
